/**
 * Ensures that API URLs have the /api prefix without duplication
 *
 * @param url - The URL to transform
 * @returns The URL with /api prefix if needed, or unchanged if it already has the prefix or is external
 *
 * @example
 * ensureApiPrefix('/staff/v2/api/users') // returns '/api/staff/v2/api/users'
 * ensureApiPrefix('/api/staff/v2/api/users') // returns '/api/staff/v2/api/users' (no change)
 * ensureApiPrefix('https://external.com/api') // returns 'https://external.com/api' (no change)
 * ensureApiPrefix('/authn/login') // returns '/authn/login' (no change)
 */
export const ensureApiPrefix = (url: string | undefined): string | undefined => {
  if (!url) {
    return url;
  }

  // Already has /api prefix - don't double it
  if (url.startsWith("/api/")) {
    return url;
  }

  // Absolute URLs (http://, https://) - don't modify
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // Auth flow URLs - don't modify (these are Django rendered pages, not API endpoints)
  if (url.startsWith("/authn/")) {
    return url;
  }

  // Relative URLs starting with / need /api prefix
  if (url.startsWith("/")) {
    // eslint-disable-next-line no-console
    console.info(`Adding prefix /api to ${url}`);
    return `/api${url}`;
  }

  // Relative paths without leading slash - return as is
  return url;
};
